import React, { useEffect, useCallback } from 'react';
import { X } from 'lucide-react';

export const Modal = ({
  open,
  onClose,
  title,
  subtitle,
  icon: Icon,
  closeDisabled = false,
  fillViewport = false,
  contentLayout = false,
  mobileFullscreen = false,
  maxWidth = 'max-w-4xl',
  headerGradient = 'from-blue-50 to-indigo-50',
  children
}) => {
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape' && !closeDisabled) onClose();
  }, [onClose, closeDisabled]);

  useEffect(() => {
    if (open) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [open, handleKeyDown]);

  if (!open) return null;

  return (
    <div
      className={`fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 ${mobileFullscreen ? 'p-0 sm:p-4' : 'p-4'}`}
      onClick={(event) => {
        if (!closeDisabled) onClose(event);
      }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
    >
      <div
        className={`bg-white shadow-2xl w-full ${maxWidth} overflow-hidden ${mobileFullscreen ? 'h-[100dvh] rounded-none sm:h-auto sm:max-h-[90vh] sm:rounded-xl' : 'rounded-xl'} ${
          fillViewport ? `${mobileFullscreen ? 'sm:h-[90vh]' : 'h-[90vh]'} flex flex-col` : 'max-h-[90vh]'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className={`flex shrink-0 items-center justify-between p-6 border-b border-gray-200 bg-gradient-to-r ${headerGradient}`}>
          <div className="flex items-center space-x-3">
            {Icon && (
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center text-white">
                <Icon size={24} />
              </div>
            )}
            <div>
              <h2 id="modal-title" className="text-xl font-bold text-gray-900">{title}</h2>
              {subtitle && <p className="text-sm text-gray-600">{subtitle}</p>}
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={closeDisabled}
            className="min-h-[44px] min-w-[44px] p-2 hover:bg-gray-100 rounded-full transition-colors focus:ring-2 focus:ring-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
            aria-label="Close modal"
          >
            <X size={20} className="text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className={fillViewport
          ? `min-h-0 flex-1 ${contentLayout ? 'flex flex-col overflow-hidden' : 'overflow-y-auto'}`
          : 'overflow-y-auto max-h-[calc(90vh-140px)]'}>
          {children}
        </div>
      </div>
    </div>
  );
};
